/**
 * One exercise as a change to it is reviewed (ai-plan-builder R15; board U): the builder's own card,
 * read-only, with what the change moves marked in place.
 *
 * The card is the day form's `ExerciseView` of the exercise AFTER the change, so it says everything
 * the builder says about it (columns, units, set types) in the builder's own words. The marks come
 * from comparing that view with the view of the exercise BEFORE, cell by cell, on the text the
 * builder prints: a value counts as changed exactly when the trainer would read a different value.
 *
 *   alter        rows paired by position: a kept row marks the cells whose value moved, a row past
 *                the old count is added („+ 5"), a row past the new count is removed (struck)
 *   added        every row is new
 *   replacement  the new exercise as it is; the one it replaces is said by the proposal, not here
 *
 * No React, no DOM, no query hook, no i18next: `t` is a parameter.
 */

import { isWarmupRole, type ExerciseDraft } from '~/entities/plan-draft';

import { toExerciseView, type ExerciseView, type ExerciseViewContext, type SetBadgeView, type SetCellView, type SetRowView, type Translate } from './dayProgramming';

const KEY = 'web:planBuilder.day.change';

/** What the exercise is compared with. */
export type ExerciseChangeBaseline =
  /** An alter: the exercise as the plan holds it now. */
  | { readonly kind: 'previous'; readonly exercise: ExerciseDraft }
  /** An exercise the change adds: every set is new. */
  | { readonly kind: 'added' }
  /** A replacement: the new exercise drawn as it is. */
  | { readonly kind: 'none' };

export interface ExerciseChangeInput extends Omit<ExerciseViewContext, 'day' | 'cycle' | 'libraryIsModal'> {
  /** The exercise as the change writes it. */
  readonly exercise: ExerciseDraft;
  readonly baseline: ExerciseChangeBaseline;
  /** The builder's marker for its place in the day (`2`, `2b`); `null` for a warm-up or when unknown. */
  readonly ordinal: string | null;
  /** The day's own rest, in seconds; `null` for the plan's default. */
  readonly dayRestSeconds: number | null;
}

export interface ChangeCellView {
  readonly key: string;
  readonly text: string;
  /** „kg", „s", or `null` for a unitless value. */
  readonly suffix: string | null;
  /**
   * The value the cell held, drawn struck before the new one; `null` when it did not change, and when
   * the cell was empty (a struck dash would read as a minus).
   */
  readonly previous: string | null;
  /** A value written into a cell that was empty: drawn alone, in full ink, as a new set is. */
  readonly isNew: boolean;
  /** The change in words for a listener („było 7, jest 8"); `null` when there is none. */
  readonly spoken: string | null;
}

export type SetRowChange = 'kept' | 'added' | 'removed';

export interface ChangeRowView {
  readonly key: string;
  /** „Seria 5": the row's name for a listener, who does not hear the `#` column's mark. */
  readonly label: string;
  /** What the `#` column prints: „2", or „+ 5" on a set the change adds. */
  readonly index: string;
  readonly change: SetRowChange;
  /** „nowa seria" / „usunięta seria", for a listener; `null` on a kept row. */
  readonly spoken: string | null;
  readonly badge: SetBadgeView;
  /** The set's type before the change, when it moved; `null` otherwise. */
  readonly previousBadge: SetBadgeView | null;
  /** That move in words („było Typ serii: Robocza, jest Typ serii: Top set"); `null` when none. */
  readonly typeSpoken: string | null;
  /** One cell per column of the exercise after the change, in its order. */
  readonly cells: readonly ChangeCellView[];
}

export interface ExerciseChangeView {
  /**
   * The card: the builder's view of the exercise after the change. On an alter its columns are both
   * sides' together, so a column the change empties still stands.
   */
  readonly exercise: ExerciseView;
  readonly rows: readonly ChangeRowView[];
}

/** What a cell reads as, unit included: the thing compared and the thing spoken. */
function shown(cell: SetCellView | undefined): string {
  if (cell === undefined || cell.text.trim() === '') return '';
  return cell.suffix === null ? cell.text : `${cell.text} ${cell.suffix}`;
}

/**
 * One cell of a kept set, compared. `now` is `undefined` in a column the change empties out of the
 * exercise: the column stays on the card so its old values can stand struck.
 */
function changedCell(t: Translate, key: string, now: SetCellView | undefined, before: SetCellView | undefined): ChangeCellView {
  const was = shown(before);
  const is = shown(now);
  const plain = { key, text: now?.text ?? '', suffix: now?.suffix ?? before?.suffix ?? null };
  if (was === is) return { ...plain, previous: null, isNew: false, spoken: null };
  if (was === '') return { ...plain, previous: null, isNew: true, spoken: t(`${KEY}.cellFilled`, { after: is }) };
  // The old value is drawn beside the new one: its unit only where the two units differ.
  const previous = is === '' || before?.suffix === now?.suffix ? (before?.text ?? '') : was;
  const spoken = is === '' ? t(`${KEY}.cellCleared`, { before: was }) : t(`${KEY}.cellChanged`, { before: was, after: is });
  return { ...plain, previous, isNew: false, spoken };
}

function plainCell(cell: SetCellView | undefined, key: string): ChangeCellView {
  return { key, text: cell?.text ?? '', suffix: cell === undefined ? null : cell.suffix, previous: null, isNew: false, spoken: null };
}

function row(
  t: Translate,
  source: SetRowView,
  change: SetRowChange,
  cells: readonly ChangeCellView[],
  previousBadge: SetBadgeView | null,
): ChangeRowView {
  return {
    key: `${change}:${source.key}`,
    label: source.label,
    index: change === 'added' ? t(`${KEY}.addedIndex`, { index: source.setIndex }) : String(source.setIndex),
    change,
    spoken: change === 'kept' ? null : t(`${KEY}.${change}`),
    badge: source.badge,
    previousBadge,
    typeSpoken: previousBadge === null ? null : t(`${KEY}.cellChanged`, { before: previousBadge.label, after: source.badge.label }),
    cells,
  };
}

export function exerciseChangeView(input: ExerciseChangeInput): ExerciseChangeView {
  const { t, baseline } = input;
  const context: ExerciseViewContext = { ...input, day: { restSeconds: input.dayRestSeconds } };
  const view = (exercise: ExerciseDraft): ExerciseView =>
    toExerciseView(context, exercise, input.ordinal, isWarmupRole(exercise.role), false);

  if (baseline.kind !== 'previous') {
    const exercise = view(input.exercise);
    const change: SetRowChange = baseline.kind === 'added' ? 'added' : 'kept';
    return {
      exercise,
      rows: exercise.rows.map((each) => row(t, each, change, each.cells.map((cell) => plainCell(cell, cell.key)), null)),
    };
  }

  // The rest column is a view of the day, not a value: shown on either side, it is compared on both,
  // so a rest the change moves reads „1:30 → 2:30" and one it leaves alone marks nothing.
  const showsRestColumn = baseline.exercise.showsRestColumn || input.exercise.showsRestColumn;
  const exercise = view({ ...input.exercise, showsRestColumn });
  const before = view({ ...baseline.exercise, showsRestColumn });
  // Every column either side has: one the change empties stays, so what it held is seen going.
  const columns = [...exercise.columns, ...before.columns.filter((column) => !exercise.columns.some((each) => each.key === column.key))];
  const cellOf = (source: SetRowView, key: string) => source.cells.find((cell) => cell.key === key);
  const kept = exercise.rows.map((each, index) => {
    const was = before.rows[index];
    if (was === undefined) return row(t, each, 'added', columns.map((column) => plainCell(cellOf(each, column.key), column.key)), null);
    const cells = columns.map((column) => changedCell(t, column.key, cellOf(each, column.key), cellOf(was, column.key)));
    return row(t, each, 'kept', cells, was.badge.letter === each.badge.letter ? null : was.badge);
  });
  // The sets the change takes away, with their values in every column they had.
  const removed = before.rows.slice(exercise.rows.length).map((was) =>
    row(t, was, 'removed', columns.map((column) => plainCell(cellOf(was, column.key), column.key)), null),
  );
  return { exercise: { ...exercise, columns }, rows: [...kept, ...removed] };
}
